import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { FormikProps, withFormik } from 'formik';
import { Form, Icon } from 'semantic-ui-react';
import styled from 'styled-components';

import { Contact } from '@/types/contact';
import { billingContactSchema } from '@/services/validators/contact';
import { classifiedSort } from '@/utils/name';
import { RequiredMark } from '@/components/shared/RequiredMark';
import { Label } from '@/components/shared/Label';

export type OuterProps = {
  contact: Contact;
  isConnecting: boolean;
  onSubmit: (contact: Contact) => void;
};

type Values = Contact;

export const BillingContactForm = withFormik<OuterProps, Values>({
  mapPropsToValues: ({ contact }) => contact,
  validationSchema: billingContactSchema,
  handleSubmit: (values, { props }) => {
    // trimの処理をapi通信側によせたい
    const contact: Contact = {
      id: values.id,
      organizationId: values.organizationId,
      corporationName: values.corporationName.trim(),
      departmentName: values.departmentName.trim(),
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      kanaFirstName: values.kanaFirstName.trim(),
      kanaLastName: values.kanaLastName.trim(),
      phoneNumber: values.phoneNumber.trim(),
      email: values.email.trim(),
      ccEmail: values.ccEmail?.trim(),
      addressee: values.addressee?.trim(),
    };
    props.onSubmit(contact);
  },
})(
  // enhanced form
  ({
    errors,
    handleSubmit,
    touched,
    values,
    isConnecting,
    setSubmitting,
    resetForm,
    setFieldTouched,
    handleChange,
  }: OuterProps & FormikProps<Values>) => {
    const { t } = useTranslation();

    useEffect(() => {
      if (isConnecting) {
        setSubmitting(false);
        resetForm({ values });
      }
    }, [isConnecting, resetForm, setSubmitting, values]);

    const onChangeWithTouched = useCallback(
      (e: React.ChangeEvent<any>) => {
        setFieldTouched(e.currentTarget.name, true);
        handleChange(e);
      },
      [handleChange, setFieldTouched],
    );

    const addresseeDescription = t('ui.expression.addresseeDescription');

    return (
      <Styled>
        <RequiredNote>
          <p>{t('ui.expression.requiredNote')}</p>
        </RequiredNote>
        <Form
          onSubmit={handleSubmit}
          className="address-form"
          id="address-form"
        >
          <Form.Field>
            <label>
              {t('models.contact.corporateName')}
              <RequiredMark />
            </label>
            <Form.Input
              data-test="corporate-name"
              className={classnames({
                error: errors.corporationName && touched.corporationName,
              })}
              fluid={true}
              placeholder={t('ui.placeholder.corporateName')}
              name="corporationName"
              onChange={onChangeWithTouched}
              value={values.corporationName}
            />
            {touched.corporationName && errors.corporationName && (
              <Label>{errors.corporationName}</Label>
            )}
          </Form.Field>
          <Form.Field>
            <label>
              {t('models.contact.department')}
              <RequiredMark />
            </label>
            <Form.Input
              data-test="department"
              className={classnames({
                error: errors.departmentName && touched.departmentName,
              })}
              fluid={true}
              placeholder={t('ui.placeholder.departmentName')}
              name="departmentName"
              onChange={onChangeWithTouched}
              value={values.departmentName}
            />
            {touched.departmentName && errors.departmentName && (
              <Label>{errors.departmentName}</Label>
            )}
          </Form.Field>
          <div className="field name-input">
            <label>
              {t('models.contact.personInCharge')}
              <RequiredMark />
            </label>
            <Form.Group widths="equal">
              {classifiedSort([
                <Form.Field key="first-name">
                  <Form.Input
                    data-test="first-name"
                    className={classnames({
                      error: errors.firstName && touched.firstName,
                    })}
                    fluid={true}
                    key="first-name"
                    placeholder={t('ui.expression.firstName')}
                    name="firstName"
                    onChange={onChangeWithTouched}
                    value={values.firstName}
                  />
                  {touched.firstName && errors.firstName && (
                    <Label classNames={'parallel-elem'}>
                      {errors.firstName}
                    </Label>
                  )}
                </Form.Field>,
                <Form.Field key="last-name">
                  <Form.Input
                    data-test="last-name"
                    className={classnames({
                      error: errors.lastName && touched.lastName,
                    })}
                    fluid={true}
                    key="last-name"
                    placeholder={t('ui.expression.lastName')}
                    name="lastName"
                    onChange={onChangeWithTouched}
                    value={values.lastName}
                  />
                  {touched.lastName && errors.lastName && (
                    <Label classNames={'parallel-elem'}>
                      {errors.lastName}
                    </Label>
                  )}
                </Form.Field>,
              ])}
            </Form.Group>
          </div>
          <div className="field">
            <label>
              {t('models.contact.phonetic')}
              {t('ui.expression.phoneticNote')}
              <RequiredMark />
            </label>
            <Form.Group widths="equal">
              {classifiedSort([
                <Form.Field key="kana-first-name">
                  <Form.Input
                    data-test="kana-first-name"
                    className={classnames({
                      error: errors.kanaFirstName && touched.kanaFirstName,
                    })}
                    fluid={true}
                    placeholder={t('ui.expression.firstNamePhonetic')}
                    name="kanaFirstName"
                    onChange={onChangeWithTouched}
                    value={values.kanaFirstName}
                  />
                  {touched.kanaFirstName && errors.kanaFirstName && (
                    <Label classNames={'parallel-elem'}>
                      {errors.kanaFirstName}
                    </Label>
                  )}
                </Form.Field>,
                <Form.Field key="kana-last-name">
                  <Form.Input
                    data-test="kana-last-name"
                    className={classnames({
                      error: errors.kanaLastName && touched.kanaLastName,
                    })}
                    fluid={true}
                    placeholder={t('ui.expression.lastNamePhonetic')}
                    name="kanaLastName"
                    onChange={onChangeWithTouched}
                    value={values.kanaLastName}
                  />
                  {touched.kanaLastName && errors.kanaLastName && (
                    <Label classNames={'parallel-elem'}>
                      {errors.kanaLastName}
                    </Label>
                  )}
                </Form.Field>,
              ])}
            </Form.Group>
          </div>
          <Form.Field>
            <label>
              {t('models.contact.phoneNumber')}
              <RequiredMark />
            </label>
            <Form.Input
              data-test="phone-number"
              className={classnames({
                error: errors.phoneNumber && touched.phoneNumber,
              })}
              fluid={true}
              placeholder={t('ui.placeholder.phoneNumber')}
              name="phoneNumber"
              onChange={onChangeWithTouched}
              value={values.phoneNumber}
            />
            {touched.phoneNumber && errors.phoneNumber && (
              <Label>{errors.phoneNumber}</Label>
            )}
          </Form.Field>
          <Form.Field>
            <label>
              {t('models.billingContact.toEmail')}
              <RequiredMark />
            </label>
            <Form.Input
              data-test="email"
              className={classnames({
                error: errors.email && touched.email,
              })}
              fluid={true}
              placeholder={t('ui.placeholder.email')}
              name="email"
              onChange={onChangeWithTouched}
              value={values.email}
            />
            {touched.email && errors.email && <Label>{errors.email}</Label>}
          </Form.Field>
          <Form.Field>
            <label>{t('models.billingContact.ccEmail')}</label>
            <Form.Input
              data-test="ccEmail"
              className={classnames({
                error: errors.ccEmail && touched.ccEmail,
              })}
              fluid={true}
              placeholder={t('ui.placeholder.ccEmail')}
              name="ccEmail"
              onChange={onChangeWithTouched}
              value={values.ccEmail}
            />
            {touched.ccEmail && errors.ccEmail && (
              <Label>{errors.ccEmail}</Label>
            )}
          </Form.Field>
          <Form.Field>
            <Form.Input
              data-test="addressee"
              className={classnames({
                error: errors.addressee && touched.addressee,
              })}
              fluid={true}
              placeholder={t('ui.placeholder.addressee')}
              label={`${t('models.billingContact.addressee')}`}
              name="addressee"
              onChange={onChangeWithTouched}
              value={values.addressee}
            />
            {errors.addressee && touched.addressee && (
              <Label>{errors.addressee}</Label>
            )}
          </Form.Field>
          {addresseeDescription && (
            <div className="addressee-hint">
              <Icon name="idea" />
              <span>{addresseeDescription}</span>
            </div>
          )}
          <button id="address-form-submit-button" style={{ display: 'none' }} />
        </Form>
      </Styled>
    );
  },
);

const Styled = styled.div`
  .ui.ui.form.address-form > .field {
    margin: 0 0 2em !important;
    line-height: 1;
  }

  .address-form .button-group {
    margin: 2em 0 3em;
  }

  .address-form .addressee-hint {
    margin-top: -1.5em;
    color: #aaa;
    font-size: 0.9em;
  }

  .ui.form.address-form .button-group .field {
    margin: 0 auto;
    text-align: center;
  }
`;

const RequiredNote = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;

  p {
    color: #9f3a38;
    font-size: 1.1em;
  }
`;
